import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Injector,
  OnChanges,
  OnInit,
  Signal,
  SimpleChanges,
  WritableSignal,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ValidatorFn, Validators } from '@angular/forms';
import {
  ControlsOf,
  FormArray,
  FormControl,
  FormGroup,
} from '@ngneat/reactive-forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { InputType } from '@vinnova/ui';
import { ValidateSpace } from '@vinnova/validation';
import { deepEqual } from 'fast-equals';
import parsePhoneNumberFromString from 'libphonenumber-js';
import {
  Observable,
  Subscription,
  distinctUntilChanged,
  of,
  skip,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { Begaran } from '../../../core/models/begaran.model';
import {
  FormPerson,
  FormPersonField,
  FormQuestionPerson,
} from '../../../core/models/form-group-mapping.model';
import { ReadonlyState } from '../../../core/models/form.model';
import {
  Person,
  QuestionStructurePerson,
} from '../../../core/models/question/question-person.model';
import { Question } from '../../../core/models/question/question.model';
import { RendererFormsQuery } from '../../../core/queries/renderer-forms.query';
import { RendererFormsRepository } from '../../../core/repositories/renderer-forms.repository';
import { BaseBegaranService } from '../../../core/services/base-begaran.service';
import { DynamicFormControlService } from '../../../core/services/dynamic-form-control.service';
import { FormValidationService } from '../../../core/services/form-validation.service';
import { PersonDetailsService } from '../../../core/services/person-details.service';
import { QuestionNumberService } from '../../../core/services/question-number.service';
import { QuestionValueChangeService } from '../../../core/services/question-value-change.service';
import { PreviousAnswersService } from '../../../core/services/previous-answers.service';

@UntilDestroy()
@Component({
  selector: 'lib-question-person',
  templateUrl: './question-person.component.html',
  styleUrls: ['./question-person.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class QuestionPersonComponent implements OnInit, OnChanges {
  question = input.required<Question>();
  labelPrefix = input<string>();
  repeaterGroupId = input.required<string>();
  questionForm = input.required<FormGroup<ControlsOf<FormQuestionPerson>>>();

  private readonly formValidationService = inject(FormValidationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly questionValueChangeService = inject(
    QuestionValueChangeService,
  );
  private readonly dynamicFormControlService = inject(
    DynamicFormControlService,
  );
  private readonly translocoService = inject(TranslocoService);
  private readonly questionNumberService = inject(QuestionNumberService);
  private readonly personDetailsService = inject(PersonDetailsService);
  private readonly formsQuery = inject(RendererFormsQuery);
  private readonly begaranService = inject(BaseBegaranService);
  private readonly rendererFormsRepository = inject(RendererFormsRepository);
  readonly previousAnswersService = inject(PreviousAnswersService);
  private readonly injector = inject(Injector);

  isChangeRequest = signal(false);
  personHasValues = signal(false);
  isProjectManager = computed(() => {
    if (
      (this.question().structure as QuestionStructurePerson).projectRole ===
      'isProjectManager'
    ) {
      return true;
    }
  });
  begaran: WritableSignal<Begaran | undefined> = signal(undefined);
  InputType = InputType;
  ReadonlyState = ReadonlyState;
  addPersonButtonHadFocus = false;
  questionNumber$!: Observable<unknown>;
  readOnly$!: Observable<ReadonlyState>;
  readOnlySignal: WritableSignal<ReadonlyState> = signal(ReadonlyState.Reader);
  activeLang: Signal<string | undefined> = toSignal(
    this.translocoService.langChanges$,
    {
      injector: this.injector,
    },
  );
  readOnlyPhoneNumbers: WritableSignal<string>[] = [];
  errorMessages$: Map<
    string,
    {
      firstName?: Observable<string | undefined>;
      lastName?: Observable<string | undefined>;
      email?: Observable<string | undefined>;
      phone?: Observable<string | undefined>;
      sex?: Observable<string | undefined>;
      role?: Observable<string | undefined>;
    }
  > = new Map<
    string,
    {
      firstName?: Observable<string | undefined>;
      lastName?: Observable<string | undefined>;
      email?: Observable<string | undefined>;
      phone?: Observable<string | undefined>;
      sex?: Observable<string | undefined>;
      role?: Observable<string | undefined>;
    }
  >();
  error = false;
  changeRequestEditPerson: WritableSignal<boolean> = signal(false);
  valueChangeSubscription!: Subscription;
  patchValueSubscription!: Subscription;
  value!: FormControl<string>;
  showChanges = input<boolean>();

  get structure(): QuestionStructurePerson {
    return this.question().structure as QuestionStructurePerson;
  }

  get persons(): FormArray<FormPerson, FormGroup<ControlsOf<FormPerson>>> {
    return this.questionForm().controls.persons;
  }

  get personsLength(): number {
    return this.persons?.length;
  }

  get allowedToAddMore() {
    return this.question().validation?.maxLength
      ? this.personsLength < (this.question().validation?.maxLength ?? 1)
      : true;
  }

  get notAllowedToRemove() {
    return this.question().validation?.required && this.personsLength === 1;
  }

  constructor() {
    effect(() => {
      this.value = this.questionForm().controls.value;
      this.value.touch$.pipe(take(1)).subscribe(() => {
        this.cdr.markForCheck();
      });
    });
  }

  ngOnInit() {
    this.isChangeRequest.set(this.rendererFormsRepository.getIsChangeRequest());
    this.readOnly$ = this.formsQuery.selectReadOnly().pipe(
      tap((val) => {
        this.readOnlySignal.set(val);
      }),
    );

    this.readOnly$.pipe(untilDestroyed(this)).subscribe();

    if (this.isChangeRequest()) {
      this.begaranService.begaran$
        .pipe(untilDestroyed(this))
        .subscribe((begaran) => {
          this.begaran.set(begaran);
        });
    }

    this.questionValueChangeService.patchInitialValue(
      this.question().id,
      this.repeaterGroupId(),
      this.value,
      this.question(),
    );

    this.setPersons();

    this.questionNumber$ = this.questionNumberService.getQuestionNumberForLabel(
      this.question().id,
    );
    if (this.value.value && this.isChangeRequest()) {
      this.updateChangeRequestPerson();
    }
    this.cdr.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['repeaterGroupId']) {
      if (this.valueChangeSubscription) {
        this.valueChangeSubscription.unsubscribe();
      }
      if (this.patchValueSubscription) {
        this.valueChangeSubscription.unsubscribe();
      }
      this.value = this.questionForm().controls.value;
      this.valueChangeSubscription = this.questionValueChangeService
        .valueChangeListener(
          this.value,
          {
            questionId: this.question().id,
            repeaterGroupId: this.repeaterGroupId(),
          },
          this.question(),
        )
        .pipe(untilDestroyed(this))
        .subscribe();

      this.patchValueSubscription = this.questionValueChangeService
        .patchValueListener(this.question(), this.repeaterGroupId(), this.value)
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          this.setPersons();
        });
    }
  }

  onAddPerson() {
    //Ej relevant för projektledare eftersom knappen är disablad
    this.addPersonToForm();
    this.setErrorMessageObservables();
    this.cdr.detectChanges();
  }

  updateChangeRequestPerson() {
    this.changeRequestEditPerson.set(!this.changeRequestEditPerson());

    this.addRequiredValidation(this.getPersonControl('firstName', 0));
    this.addRequiredValidation(this.getPersonControl('lastName', 0));
    this.addRequiredValidation(this.getPersonControl('email', 0));
    if (this.isProjectManager()) {
      this.addRequiredValidation(this.getPersonControl('phone', 0));
      this.addRequiredValidation(this.getPersonControl('sex', 0));
    }
  }

  onRemovePerson(index: number) {
    if (this.isChangeRequest()) {
      this.changeRequestEditPerson.set(false);
      this.resetPersonForm(index);
      this.questionForm().controls.value.patchValue('');
    } else {
      this.removePersonFormArray(index);
      this.updateValue();
    }
    this.cdr.detectChanges();
  }

  getPersonSexValue(genderValue: string): string {
    switch (genderValue) {
      case '1': {
        return this.translocoService.translate('person_sex_male');
      }
      case '2': {
        return this.translocoService.translate('person_sex_female');
      }
      case '0': {
        return this.translocoService.translate('person_sex_other');
      }
      default: {
        return '';
      }
    }
  }

  private addRequiredValidation(control: FormControl<string>) {
    control.addValidators([Validators.required, ValidateSpace as ValidatorFn]);
    control.updateValueAndValidity();
  }

  private resetPersonForm(index: number) {
    this.resetValueAndValidation(this.getPersonControl('firstName', index));
    this.resetValueAndValidation(this.getPersonControl('lastName', index));
    this.resetValueAndValidation(this.getPersonControl('email', index));
    this.resetValueAndValidation(this.getPersonControl('phone', index));
    this.resetValueAndValidation(this.getPersonControl('sex', index));
    this.resetValueAndValidation(this.getPersonControl('role', index));
    this.personHasValues.set(false);
  }

  private getPersonControl(name: string, index: number): FormControl<string> {
    return this.persons.controls[index].get(name) as FormControl<string>;
  }

  private resetValueAndValidation(control: FormControl<string>) {
    control.removeValidators([
      Validators.required,
      ValidateSpace as ValidatorFn,
    ]);
    control.updateValueAndValidity();
    control.setValue('');
    control.markAsUntouched();
    control.markAsPristine();
  }

  private removePersonFormArray(index: number) {
    /**
     * FormArray behaved strange when removing person with removeAt when not the last in the list
     */
    if (index < this.persons.length - 1) {
      const value = this.persons.getRawValue();
      /**
       * Put the element that should be removed last in the FormArray so that it can be removed
       * without causing problem with the indexes for other items.
       */
      this.persons.setValue(
        value
          .slice(0, index)
          // eslint-disable-next-line unicorn/prefer-spread
          .concat(value.slice(index + 1))
          // eslint-disable-next-line unicorn/prefer-spread
          .concat(value[index]),
      );
      this.persons.removeAt(value.length - 1);
    } else {
      this.persons.removeAt(index);
    }
  }

  private addPersonToForm(person?: Person) {
    const personForm = this.dynamicFormControlService.createPersonFormGroup(
      this.question().validation?.required ?? false,
      person,
      this.structure,
    );
    this.personChanged(personForm);
    this.persons.push(personForm);

    this.readOnlyPhoneNumbers.push(
      signal(this.formatPhoneNumber(personForm.controls.phone.value)),
    );
  }

  private formatPhoneNumber(phone: string | null | undefined): string {
    if (!phone) {
      return '';
    }
    const parsedPhoneNumber = parsePhoneNumberFromString(phone);
    return parsedPhoneNumber ? parsedPhoneNumber.formatNational() : '';
  }

  private personChanged(form: FormGroup<ControlsOf<FormPerson>>) {
    form.valueChanges
      .pipe(
        distinctUntilChanged((prev, curr) => deepEqual(prev, curr)),
        skip(1),
        untilDestroyed(this),
      )
      .subscribe(() => this.updateValue());
  }

  private setPersons() {
    const persons = this.value.value
      ? (JSON.parse(this.value.value) as [])
      : [];
    const structure = this.question().structure as QuestionStructurePerson;
    this.persons.clear();
    this.errorMessages$.clear();
    this.readOnlyPhoneNumbers = [];
    if (persons.length > 0) {
      for (const person of persons) {
        this.addPersonToForm(person);
        if (this.isChangeRequest()) {
          this.personHasValues.set(this.checkIfPersonHasValues(person));
        }
      }
    } else if (
      structure.projectRole === 'isProjectManager' ||
      structure.projectRole === 'isSignatory'
    ) {
      //Finns inget ifyllt lägger vi till en tom komponent
      this.addPersonToForm();
    }

    this.setErrorMessageObservables();
    this.cdr.markForCheck();
  }

  private checkIfPersonHasValues(person: Person): boolean {
    return (
      person.firstName != undefined ||
      person.lastName != undefined ||
      person.email != undefined ||
      person.phone != undefined ||
      person.sex != undefined
    );
  }

  private updateValue() {
    const personsValue = [];
    for (let index = 0; index < this.persons.controls.length; index++) {
      const personForm = this.persons.controls[index];
      const person = {
        id: personForm.value.id,
        email: this.structure.email ? personForm.value.email : undefined,
        firstName: this.structure.firstName
          ? personForm.value.firstName
          : undefined,
        lastName: this.structure.lastName
          ? personForm.value.lastName
          : undefined,
        phone: this.structure.phone ? personForm.value.phone : undefined,
        sex: this.structure.sex ? personForm.value.sex : undefined,
        role: this.structure.role ? personForm.value.role : undefined,
      } as Person;
      personsValue.push(person);

      this.readOnlyPhoneNumbers[index].set(
        this.formatPhoneNumber(personForm.controls.phone.value),
      );
    }
    this.value.patchValue(JSON.stringify(personsValue));
    this.value.markAllAsTouched();
    this.value.markAsDirty();
    this.cdr.detectChanges();
    this.formValidationService.triggerSectionInvalidCheck(this.question().id);
  }

  getInformationDetails(personForm: FormGroup<ControlsOf<FormPerson>>) {
    this.personDetailsService
      .getPersonDetails$()
      .pipe(take(1))
      .subscribe((person) => {
        personForm.patchValue(person);
        personForm.get('firstName').markAsDirty();
        personForm.get('firstName').markAsTouched();
      });
  }

  private setErrorMessageObservables() {
    const fields: FormPersonField[] = [
      'firstName',
      'lastName',
      'email',
      'phone',
      'sex',
      'role',
    ];
    const messages: Record<FormPersonField, string | [string, string]> = {
      firstName: 'required',
      lastName: 'required',
      email: ['person_email_invalid', 'required'],
      phone: ['phone_invalid', 'required'],
      sex: 'radiobuttons_required',
      role: 'required',
    };

    for (const personForm of this.persons.controls) {
      if (this.errorMessages$.has(personForm.value.id)) continue;

      const personValidation: Partial<
        Record<FormPersonField, Observable<string | undefined>>
      > = {};
      for (const field of fields) {
        if (this.structure[field]) {
          personValidation[field] = this.readOnly$.pipe(
            switchMap((readonly) => {
              switch (readonly) {
                case ReadonlyState.Editable: {
                  return this.formValidationService
                    .questionInvalid(
                      personForm.get(field) as FormControl<string>,
                      this.question().id,
                    )
                    .pipe(
                      switchMap((invalid) => {
                        if (!invalid) return of(undefined);
                        const control = personForm.get(
                          field,
                        ) as FormControl<string>;
                        return field === 'phone' || field === 'email'
                          ? this.translocoService.selectTranslate(
                              control.errors?.validEmail ||
                                control.errors?.validPhoneNumber
                                ? messages[field][0]
                                : messages[field][1],
                            )
                          : this.translocoService.selectTranslate(
                              messages[field] as string,
                            );
                      }),
                    );
                }
                case ReadonlyState.Locked: {
                  return this.errorMessage(
                    personForm.get(field) as FormControl<string>,
                  );
                }
                case ReadonlyState.Reader: {
                  return of(undefined);
                }
                default: {
                  return of(undefined);
                }
              }
            }),
          );
        }
      }
      this.errorMessages$.set(personForm.value.id, personValidation);
    }
  }

  private errorMessage(
    formControl: FormControl<string>,
  ): Observable<string | undefined> {
    if (formControl.errors?.validPhoneNumber) {
      this.error = true;
      return this.translocoService.selectTranslate('phone_invalid');
    }
    if (formControl.errors?.required) {
      this.error = true;
      return this.translocoService.selectTranslate('field_required');
    }
    if (formControl.errors?.validEmail) {
      this.error = true;
      return this.translocoService.selectTranslate('person_email_invalid');
    }
    if (formControl.errors?.validPhoneNumber) {
      this.error = true;
      return this.translocoService.selectTranslate('phone_invalid');
    }
    return of(undefined);
  }
}
